import { IntulseIntegration } from "@/schema/intulse-integration"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import { apiContextFetch } from "@/hooks/api/base"
import { useApiContext } from "@/hooks/api/use-api-context"
import { useBookId } from "@/hooks/use-book-id"

interface AuthUriResponse {
  auth_uri: string
}

export const INTULSE_INTEGRATIONS_QUERY_KEY = ["intulse-integrations"]

export function useFetchIntulseIntegrations(enabled = true) {
  const getContext = useApiContext()
  const bookId = useBookId()

  return useQuery<IntulseIntegration[]>({
    queryKey: [...INTULSE_INTEGRATIONS_QUERY_KEY, bookId],
    queryFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No context found")

      const data = await apiContextFetch<IntulseIntegration[]>(context, "intulse_integrations")
      return data
    },
    enabled,
  })
}

export function useGetIntulseAuthUri() {
  const getContext = useApiContext()

  return useMutation<AuthUriResponse, Error, { returnUrl?: string } | void>({
    mutationKey: ["intulse-auth-uri"],
    mutationFn: async (params = {}) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")

      const searchParams = new URLSearchParams()
      if (params && "returnUrl" in params && params.returnUrl) {
        searchParams.append("return_url", params.returnUrl)
      }

      const url = `intulse_integrations/get_auth_uri${searchParams.toString() ? `?${searchParams.toString()}` : ""}`
      return apiContextFetch<AuthUriResponse>(context, url)
    },
  })
}

export function useDisconnectIntulseIntegration() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation<void, Error, { id: string }>({
    mutationKey: ["intulse-integration-disconnect"],
    mutationFn: async ({ id }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")

      await apiContextFetch(context, `intulse_integrations/${id}`, {
        method: "PUT",
        body: JSON.stringify({ disconnect: true }),
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: INTULSE_INTEGRATIONS_QUERY_KEY })
      queryClient.invalidateQueries({ queryKey: ["intulse-credentials"] })
      queryClient.invalidateQueries({ queryKey: ["intulse-directory"] })
      queryClient.invalidateQueries({ queryKey: ["sms-messages"] })
      queryClient.invalidateQueries({ queryKey: ["call-logs"] })
    },
  })
}

export function useConfigureIntulseSMSHistory() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation<IntulseIntegration, Error, { id: string; organizationApiKey: string }>({
    mutationKey: ["intulse-sms-history-configure"],
    mutationFn: async ({ id, organizationApiKey }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")

      return apiContextFetch<IntulseIntegration>(context, `intulse_integrations/${id}`, {
        method: "PUT",
        body: JSON.stringify({ master_api_key: organizationApiKey }),
      })
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: INTULSE_INTEGRATIONS_QUERY_KEY })
      queryClient.invalidateQueries({ queryKey: ["intulse-credentials"] })
      queryClient.invalidateQueries({ queryKey: ["intulse-directory"] })
    },
  })
}

export function useDeleteIntulseIntegration() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation<void, Error, { id: string }>({
    mutationFn: async ({ id }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")

      await apiContextFetch(context, `intulse_integrations/${id}`, {
        method: "DELETE",
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: INTULSE_INTEGRATIONS_QUERY_KEY })
      queryClient.invalidateQueries({ queryKey: ["intulse-credentials"] })
      queryClient.invalidateQueries({ queryKey: ["intulse-directory"] })
      queryClient.invalidateQueries({ queryKey: ["sms-messages"] })
      queryClient.invalidateQueries({ queryKey: ["call-logs"] })
    },
  })
}

export interface IntulseOrganizationCredential {
  id: string
  intulse_client_id: string
}

export interface IntulseDirectory {
  extensions: { Exten: string; Name?: string }[]
  phone_numbers: string[]
}

export function useFetchIntulseCredentials(enabled: boolean) {
  const getContext = useApiContext()
  const bookId = useBookId()
  return useQuery({
    queryKey: ["intulse-credentials", bookId],
    enabled,
    queryFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return apiContextFetch<IntulseOrganizationCredential[]>(context, "intulse_credentials")
    },
  })
}

export function useFetchIntulseDirectory(credentialId: string) {
  const getContext = useApiContext()
  return useQuery({
    queryKey: ["intulse-directory", credentialId],
    enabled: !!credentialId,
    retry: false,
    queryFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return apiContextFetch<IntulseDirectory>(context, `intulse_credentials/${credentialId}`)
    },
  })
}

export function useConfigureIntulseOrganization() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (params: {
      integration_id?: string
      intulse_credential_id: string
      selected_extensions: string[]
      sms_lines: { phone_number: string; allowed_user_ids: string[] }[]
    }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return apiContextFetch<IntulseIntegration>(
        context,
        params.integration_id ? `intulse_integrations/${params.integration_id}` : "intulse_integrations",
        {
          method: params.integration_id ? "PUT" : "POST",
          body: JSON.stringify(params),
        }
      )
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: INTULSE_INTEGRATIONS_QUERY_KEY })
      queryClient.invalidateQueries({ queryKey: ["intulse-credentials"] })
      queryClient.invalidateQueries({ queryKey: ["intulse-directory"] })
      queryClient.invalidateQueries({ queryKey: ["sms-messages"] })
      queryClient.invalidateQueries({ queryKey: ["call-logs"] })
      queryClient.invalidateQueries({ queryKey: ["phone-number-configs"] })
    },
  })
}
